import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

const NAV = [
  { id: "about", label: "About" },
  { id: "products", label: "Products" },
  { id: "classina", label: "Classina®" },
  { id: "capabilities", label: "Capabilities" },
  { id: "why", label: "Why SBK" },
  { id: "contact", label: "Contact" },
];

const Navbar = ({ transparentOnTop = true }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showSolid = !transparentOnTop || scrolled;

  const linkHref = (id) => (onHome ? `#${id}` : `/#${id}`);
  const productsHref = onHome ? "#products" : "/products";
  const classinaHref = "/classina";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        showSolid
          ? "backdrop-blur-xl bg-stone-50/85 border-b border-stone-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={showSolid ? "/sbk-logo.png" : "/sbk-logo-white.png"}
            alt="SBK Manufacturing Company"
            className="h-10 md:h-12 w-auto select-none transition-opacity duration-300"
            draggable="false"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => {
            const href =
              n.id === "products"
                ? productsHref
                : n.id === "classina"
                ? classinaHref
                : linkHref(n.id);
            const isInternalRoute =
              (n.id === "products" && !onHome) || n.id === "classina";
            const linkClass =
              "text-sm text-ink-900/80 hover:text-ink-900 transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-ink-900 hover:after:w-full after:transition-all after:duration-300";
            return isInternalRoute ? (
              <Link key={n.id} to={href} data-testid={`nav-link-${n.id}`} className={linkClass}>
                {n.label}
              </Link>
            ) : (
              <a key={n.id} href={href} data-testid={`nav-link-${n.id}`} className={linkClass}>
                {n.label}
              </a>
            );
          })}
          <a
            href={onHome ? "#contact" : "/#contact"}
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 bg-ink-900 text-stone-50 px-5 py-2.5 text-sm rounded-sm hover:bg-ink-800 transition-colors"
          >
            Request a Quote <ArrowUpRight className="w-4 h-4" />
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-ink-900"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-stone-50 border-t border-stone-200">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => {
              const href =
                n.id === "products"
                  ? productsHref
                  : n.id === "classina"
                  ? classinaHref
                  : linkHref(n.id);
              return (
                <a
                  key={n.id}
                  href={href}
                  data-testid={`mobile-nav-link-${n.id}`}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-ink-900"
                >
                  {n.label}
                </a>
              );
            })}
            <a
              href={onHome ? "#contact" : "/#contact"}
              data-testid="mobile-nav-cta"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-ink-900 text-stone-50 px-5 py-3 text-sm rounded-sm"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
